import { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import InputAdornment from "@mui/material/InputAdornment";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import SearchIcon from "@mui/icons-material/Search";
import { findByTurma } from "../AlunoTurma/api-aluno-turma";
import { errorMessage } from "../../util/ConfirmAlertUtil";
import Loading from "../UI/Loading";

const normalize = (texto = "") =>
  texto
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

/**
 * Diálogo para escolher um aluno da turma que ainda não está na atividade.
 * `codigosNaAtividade`: códigos de AlunoTurma já vinculados.
 */
export default function AtividadeAlunoForm({ open, onClose, codTurma, codigosNaAtividade, onSelect }) {
  const [alunosTurma, setAlunosTurma] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [busca, setBusca] = useState("");

  useEffect(() => {
    if (!open || !codTurma) return undefined;
    let active = true;
    setIsLoading(true);
    setBusca("");
    findByTurma(codTurma)
      .then((data) => {
        if (!active || !data) return;
        if (data.error) {
          errorMessage(data.message);
        } else if (Array.isArray(data)) {
          setAlunosTurma(data);
        }
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open, codTurma]);

  const disponiveis = useMemo(() => {
    const termo = normalize(busca.trim());
    return alunosTurma
      .filter((at) => at.ativo !== false && !codigosNaAtividade.has(at.codigo))
      .filter((at) => !termo || normalize(at.aluno?.nome).includes(termo))
      .sort((a, b) => (a.numeroChamada ?? Infinity) - (b.numeroChamada ?? Infinity));
  }, [alunosTurma, codigosNaAtividade, busca]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Adicionar Aluno na Atividade</DialogTitle>
      <DialogContent>
        <TextField
          size="small"
          fullWidth
          autoFocus
          placeholder="Buscar aluno"
          value={busca}
          onChange={(event) => setBusca(event.target.value)}
          sx={{ mb: 1.5 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
        {isLoading ? (
          <Box py={4}>
            <Loading size={28} description="Carregando alunos..." />
          </Box>
        ) : disponiveis.length === 0 ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
            {busca ? "Nenhum aluno encontrado." : "Todos os alunos ativos da turma já estão na atividade."}
          </Typography>
        ) : (
          <List dense sx={{ maxHeight: 360, overflowY: "auto", border: 1, borderColor: "divider", borderRadius: 2 }}>
            {disponiveis.map((at) => (
              <ListItemButton key={at.codigo} divider onClick={() => onSelect(at)}>
                <ListItemText
                  primary={at.aluno?.nome}
                  secondary={at.numeroChamada ? `Nº ${at.numeroChamada}` : undefined}
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}
